import { useQuery } from '@tanstack/react-query'
import { api } from '@/api/client'

// ---------- Types ----------

export interface SystemStatusResponse {
  status: string
  timestamp: string
  version: string
  environment: string
  infrastructure: {
    database: { status: string; latency_ms?: number }
    redis: { status: string; latency_ms?: number }
    weaviate: { status: string; latency_ms?: number }
    scheduler: {
      status: string
      job_count: number
      jobs: Array<{
        id: string
        name: string
        next_run: string | null
        pending: boolean
      }>
    }
  }
  agents: Array<{
    agent_id: string
    model: string
    enabled: boolean
    is_default: boolean
    vertical_id: string | null
    prompt_version: string
  }>
  platform: {
    organizations: Array<{
      name: string
      slug: string
      vertical: string | null
      user_count: number
      is_active: boolean
    }>
    verticals: Array<{
      slug: string
      display_name: string
      is_active: boolean
      org_count: number
    }>
    total_users: number
  }
  database_state: {
    alembic_version: string
    rls_policies: number
    rls_tables: string[]
    row_counts: Record<string, number>
  }
  feature_flags: Record<string, boolean>
}

// ---------- Hook ----------

export function useSystemStatus() {
  return useQuery<SystemStatusResponse>({
    queryKey: ['admin', 'system-status'],
    queryFn: () => api.get('/api/v1/admin/system-status'),
    refetchInterval: 60 * 1000,
  })
}

// ---------- Helpers ----------

export function toStatusDotStatus(
  status: string
): 'healthy' | 'degraded' | 'down' | 'unknown' {
  const s = status.toLowerCase()
  if (s === 'healthy' || s === 'running') return 'healthy'
  if (
    s === 'degraded' ||
    s === 'unconfigured' ||
    s === 'stopped' ||
    s === 'not_initialized'
  )
    return 'degraded'
  if (s === 'unhealthy') return 'down'
  return 'unknown'
}

export function formatAgentVertical(agentId: string): string {
  if (agentId === 'orchestrator') return 'Platform'
  if (agentId.startsWith('ca_')) return 'CA Copilot'
  if (agentId.startsWith('cr_')) return 'CR Ops'
  return agentId
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

export function formatFlagName(key: string): string {
  return key
    .replace(/_enabled$/, '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
}
